import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSwipeable } from 'react-swipeable';
import axios from 'axios';
import { CommentSection } from 'react-comments-section';
import './Comment.css';
import '../CommentSection/Styles/App.scss';
import Comment from '../CommentSection/Comment';
import AddComment from '../CommentSection/AddComment';

const config = require('../../config');

// --- Helper Icons ---
const IconPlaceholder = ({ label, count, onClick }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '15px', cursor: 'pointer' }} onClick={onClick}>
        <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            marginBottom: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            color: 'white'
        }}>
            {label}
        </div>
        {count !== undefined && <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold', textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}>{count}</span>}
    </div>
);

const SoundIconPlaceholder = ({ imageUrl = 'https://via.placeholder.com/50' }) => (
    <div style={{
        width: '45px',
        height: '45px',
        borderRadius: '50%',
        border: '2px solid rgba(255, 255, 255, 0.5)',
        padding: '3px',
        marginTop: '10px'
    }}>
        <img
            src={imageUrl}
            alt="Sound cover"
            style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
        />
    </div>
);

// --- Styles (adapted from PopupV4) ---
const styles = {
    reelContainer: {
        width: '100%',
        height: '100%',
        backgroundColor: '#111',
        margin: '0',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '16px', // Rounded corners for the drawer feel
        display: 'flex',
        flexDirection: 'column',
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
        display: 'flex',
        pointerEvents: 'none',
    },
    topNav: {
        position: 'absolute',
        top: '15px',
        left: 0,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        gap: '15px',
        color: 'rgba(255, 255, 255, 0.7)',
        fontWeight: 'bold',
        fontSize: '16px',
        textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
        pointerEvents: 'auto',
        zIndex: 5,
    },
    sidebar: {
        position: 'absolute',
        bottom: '15px',
        right: '10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pointerEvents: 'auto',
    },
    bottomInfo: {
        position: 'absolute',
        bottom: '15px',
        left: '15px',
        right: '80px',
        color: 'white',
        textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
        pointerEvents: 'auto',
    },
    username: {
        fontWeight: 'bold',
        fontSize: '20px', // Slightly larger for emphasis
        marginBottom: '5px',
        display: 'flex',
        alignItems: 'center',
        gap: '5px'
    },
    caption: {
        fontSize: '14px',
        marginBottom: '10px',
        lineHeight: '1.3',
        maxHeight: '6em',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    soundInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
    },
    closeButton: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 20,
        background: 'rgba(0, 0, 0, 0.5)',
        color: 'white',
        border: 'none',
        borderRadius: '50%',
        width: '35px',
        height: '35px',
        fontSize: '24px',
        lineHeight: '40px',
        textAlign: 'center',
        cursor: 'pointer',
        pointerEvents: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    commentSectionContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '65%',
        backgroundColor: '#FFFFFF',
        color: '#000000',
        zIndex: 15,
        overflowY: 'hidden',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px -2px 10px rgba(0,0,0,0.1)',
        transition: 'transform 0.3s ease-out',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
    },
    commentSectionHeader: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '8px',
        borderBottom: '1px solid #f0f0f0',
        backgroundColor: '#f9f9f9',
        minHeight: '25px',
        position: 'relative',
        cursor: 'grab',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
    },
    commentDragIcon: {
        width: '50px',
        height: '6px',
        backgroundColor: '#cccccc',
        borderRadius: '5px',
    },
    commentSectionContent: {
        flexGrow: 1,
        overflowY: 'auto',
        paddingBottom: '100px',
        overflowX: 'hidden',
    },
    addCommentContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        backgroundColor: '#fff',
        padding: '10px',
        borderTop: '1px solid #e0e0e0',
        zIndex: 1000,
    },
    dot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255, 255, 255, 0.4)',
        boxShadow: '0 0 2px rgba(0, 0, 0, 0.5)',
    },
    activeDot: {
        backgroundColor: 'white',
    },
    dotsContainer: {
        position: 'absolute',
        bottom: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
        zIndex: 3,
    },
};

const PopupDrawerV2 = ({ property, onClose, goToPreviousMarker, goToNextMarker }) => {
    const [isPopupOpen, setIsPopupOpen] = useState(true);
    const audioRef = useRef(null);
    const videoRef = useRef(null);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [likeCount, setLikeCount] = useState(property.likeCount || 0);
    const [commentPanelVisible, setCommentPanelVisible] = useState(false);
    const [rawComments, setRawComments] = useState([]);
    const [commentLikes, setCommentLikes] = useState({});
    const [deleteModalState, setDeleteModalState] = useState(false);

    const [startTouchY, setStartTouchY] = useState(null);

    const API_ENDPOINT = config.getAllURL + 'api/comment';

    // --- Effects ---
    const isUnmounting = useRef(false);
    useEffect(() => {
        return () => { isUnmounting.current = true; cleanupAudio(); };
    }, []);

    const cleanupAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.removeAttribute('src');
            audioRef.current.load();
            audioRef.current = null;
            setIsAudioPlaying(false);
        }
    };

    useEffect(() => {
        const fetchComments = async () => {
            if (!property._id) return;
            try {
                const response = await axios.get(`${API_ENDPOINT}?mapMarker=${property._id}`);
                setRawComments(response.data || []);
            } catch (error) {
                setRawComments([]);
            }
        };
        fetchComments();
    }, [property._id, API_ENDPOINT, commentPanelVisible]);

    useEffect(() => {
        const currentMedia = property.media?.[currentMediaIndex];
        if (!currentMedia) return;

        // Logic for playing audio/video
        const soundtrackUrl = property.soundtrackUrl ? `https://${property.soundtrackUrl}` : null;
        const isImage = currentMedia.type === 'image' || /\.(png|jpe?g|gif|webp)$/i.test(currentMedia.url);

        if (soundtrackUrl && isImage) {
            if (!audioRef.current) {
                audioRef.current = new Audio(soundtrackUrl);
                audioRef.current.loop = true;
                audioRef.current.volume = 0.5;
                audioRef.current.onplaying = () => setIsAudioPlaying(true);
                audioRef.current.onpause = () => setIsAudioPlaying(false);
                audioRef.current.play().catch(() => { });
            }
        } else {
            cleanupAudio();
        }
        return () => {
            if (!isImage) cleanupAudio();
        };
    }, [property.soundtrackUrl, property.media, currentMediaIndex]);

    // --- Handlers ---
    const handleLike = async () => {
        const token = localStorage.getItem('token');
        if (!token) return alert('Please login to like');
        try {
            const checkResp = await axios.post(`${config.getAllURL}api/like/checkLike`, { mapMarker: property._id }, { headers: { Authorization: `Bearer ${token}` } });
            const alreadyLiked = checkResp.data.liked;
            await axios.post(`${config.getAllURL}api/like`, { mapMarker: property._id, liked: !alreadyLiked }, { headers: { Authorization: `Bearer ${token}` } });
            setLikeCount(prev => alreadyLiked ? prev - 1 : prev + 1);
        } catch {
            alert('Failed to update like');
        }
    };

    const handleCommentIconClick = () => setCommentPanelVisible(!commentPanelVisible);
    const handleSave = () => alert('Save not implemented');
    const handleShare = () => alert('Share not implemented');

    const handlePrevMedia = (e) => {
        e?.stopPropagation();
        if (property.media?.length) setCurrentMediaIndex(prev => (prev === 0 ? property.media.length - 1 : prev - 1));
    };
    const handleNextMedia = (e) => {
        e?.stopPropagation();
        if (property.media?.length) setCurrentMediaIndex(prev => (prev + 1) % property.media.length);
    };

    // --- Comments Transformation ---
    const transformCommentsForSection = (apiComments) => {
        if (!Array.isArray(apiComments)) return [];
        const commentMap = {};
        const topLevelComments = [];
        apiComments.forEach(comment => {
            const authorName = comment.author?.username || 'Anonymous';
            commentMap[comment._id] = {
                userId: comment.author?._id || 'unknownUserId',
                comId: comment._id,
                fullName: authorName,
                avatarUrl: comment.author?.profilePicture || `https://ui-avatars.com/api/?name=${encodeURIComponent(authorName)}&background=random&color=fff`,
                text: comment.content,
                timestamp: comment.createdAt,
                replies: [],
                likeCount: comment.likeCount || 0,
                commentId: comment._id,
                content: comment.content,
                username: authorName,
                score: comment.likeCount || 0,
            };
        });
        apiComments.forEach(comment => {
            if (comment.parentComment && commentMap[comment.parentComment._id]) {
                commentMap[comment.parentComment._id].replies.push(commentMap[comment._id]);
            } else if (!comment.parentComment) {
                topLevelComments.push(commentMap[comment._id]);
            }
        });
        return topLevelComments;
    };
    const commentDataForSection = useMemo(() => transformCommentsForSection(rawComments), [rawComments]);

    // --- Swipe / Close Handlers ---
    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => handleNextMedia(),
        onSwipedRight: () => handlePrevMedia(),
        onSwipedUp: () => goToNextMarker && goToNextMarker(),
        onSwipedDown: (e) => {
            // If we are in drawer mode, swiping down might mean closing the drawer or going to prev marker
            // The user wants "PopupDrawerV2" to replace expanded content.
            // If closing is desired on swipe down:
            if (onClose) onClose();
            else if (goToPreviousMarker) goToPreviousMarker();
        },
        preventDefaultTouchmoveEvent: true,
        trackMouse: true,
    });

    const timeAgo = (dateString) => {
        if (!dateString) return '';
        const diff = Math.floor((Date.now() - new Date(dateString)) / 1000);
        if (diff < 60) return `${diff}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    };

    const currentMedia = property.media?.[currentMediaIndex];

    // --- Render ---
    // Note: We render with a fixed 70% width/height overlay
    return (
        <div
            style={{
                position: 'fixed',
                // top: 0,
                // left: 0,
                width: '100dvw',
                height: '100dvh',
                backgroundColor: 'rgba(250,250,250,0.2)', // Darken background behind popup
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            onClick={(e) => {
                if (commentPanelVisible) setCommentPanelVisible(false);
                else if (onClose) onClose();
            }}
        >
            <div
                style={{
                    ...styles.reelContainer,
                    top: '-20px',
                    width: '95vw',
                    height: '90dvh',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* <button style={styles.closeButton} onClick={onClose} aria-label="Close popup">&times;</button> */}
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '40px',
                    height: '5px',
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: '5px',
                    zIndex: 20,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} />

                <div {...swipeHandlers} style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', touchAction: 'pan-y' }}>
                    {currentMedia?.url && (
                        currentMedia.type === 'video' ? (
                            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#000' }}>
                                <video
                                    src={`https://${currentMedia.url}`}
                                    poster={property.thumbnailUrl || ''}
                                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                                    autoPlay loop playsInline muted={false}
                                    ref={videoRef}
                                />
                            </div>
                        ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#000' }}>
                                <img
                                    src={`https://${currentMedia.url}`}
                                    alt={property.title}
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                            </div>
                        )
                    )}
                    {/* Dots */}
                    <div style={styles.dotsContainer}>
                        {property.media.map((_, index) => (
                            <div key={index} style={{ ...styles.dot, ...(index === currentMediaIndex ? styles.activeDot : {}) }}></div>
                        ))}
                    </div>
                </div>

                {/* Overlay Info */}
                <div style={styles.overlay}>
                    <div style={styles.topNav}>
                        <span>Following</span>
                        <span>For You</span>
                    </div>

                    <div style={styles.bottomInfo}>
                        <div style={styles.username}>
                            <span>{property.title || property.author?.username || 'User'}</span>
                        </div>
                        <div style={styles.caption}>{property.description}</div>
                        <div style={styles.soundInfo}>
                            <span>📅</span>
                            <span>{timeAgo(property.createdAt)}</span>
                        </div>
                    </div>

                    <div style={styles.sidebar}>
                        <div style={{ position: 'relative', marginBottom: '20px' }}>
                            <img src={property.userProfileImage || `https://ui-avatars.com/api/?name=${encodeURIComponent(property.author?.username || 'U')}`} alt="User"
                                style={{ width: '50px', height: '50px', borderRadius: '50%', border: '2px solid white', objectFit: 'cover' }} />
                        </div>
                        <IconPlaceholder label="❤️" count={likeCount} onClick={handleLike} />
                        <IconPlaceholder label="💬" count={commentDataForSection?.length || 0} onClick={handleCommentIconClick} />
                        <IconPlaceholder label="💾" onClick={handleSave} />
                        <IconPlaceholder label="➤" onClick={handleShare} />
                        <SoundIconPlaceholder imageUrl={property.soundImageUrl || 'https://via.placeholder.com/50'} />
                    </div>
                </div>

                {/* Comments */}
                {commentPanelVisible && (
                    <div style={styles.commentSectionContainer} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.commentSectionHeader} onClick={() => setCommentPanelVisible(false)}>
                            <div style={styles.commentDragIcon} />
                        </div>
                        <div style={styles.commentSectionContent}>
                            {commentDataForSection.map((c) => (
                                <Comment
                                    key={c.comId}
                                    commentData={c}
                                />
                            ))}
                        </div>
                        <div style={styles.addCommentContainer}>
                            <AddComment buttonValue="send" onSubmit={() => alert('Add comment logic')} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PopupDrawerV2;
